import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Users, Moon, ChevronRight, CalendarDays } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Header } from "../../../components/page-header/Header";
import { useBulkPrayertimesQuery } from "../../../store/Reducer/prayertimes";
import Label from "../../../components/labels/Labels";
import MonthDropdown from "../../../components/dropdown/MonthDropdown";
import PrayerTimeTableSkeleton from "../user-settings/PrayerTimeTableSkeleton";
import { athanOf, iqamahOf } from "../user-settings/prayer-time-table";
import useMasjidFormat from "../../../hooks/useMasjidFormat";

const PRAYERS = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];
const monthOptions = [...Array(12).keys()].map((i) => ({
  id: i + 1,
  name: new Date(0, i).toLocaleString("default", { month: "long" }),
}));
const pad = (n) => String(n).padStart(2, "0");
const dashed = (d) => `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;

const Card = ({ to, icon, tone, title, text, children }) => (
  <Link to={to} className="group relative rounded-xl bg-white p-6 shadow-lg transition-all hover:shadow-xl">
    <div className="flex items-start justify-between gap-3">
      <div className={`inline-flex h-11 w-11 items-center justify-center rounded-full ${tone}`}>{icon}</div>
      <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-slate-600" />
    </div>
    <h3 className="mt-3 text-lg font-semibold text-slate-800">{title}</h3>
    <p className="mt-0.5 text-sm text-slate-600">{text}</p>
    {children && <div className="mt-3 text-sm text-slate-700">{children}</div>}
  </Link>
);

const PrayerTimeMain = () => {
  const { t } = useTranslation();
  // Shown in the masjid's time and date format; the CSV keeps 24-hour times.
  const f = useMasjidFormat();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[now.getMonth()]);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const validYear = selectedYear >= 2000 && selectedYear <= 2100;

  const { data, isFetching, error } = useBulkPrayertimesQuery(
    { month: selectedMonth.id, year: selectedYear },
    { skip: !validYear }
  );
  // Today's times, for the cards (always this month's data, whatever is selected).
  const { data: thisMonth } = useBulkPrayertimesQuery({ month: now.getMonth() + 1, year: now.getFullYear() });

  const days = data?.data?.prayerTimes || [];
  const today = (thisMonth?.data?.prayerTimes || []).find((d) => d.date?.gregorian?.date === dashed(now));
  const jummah = thisMonth?.data?.jummahTimes || [];
  const eid = (thisMonth?.data?.specialSalahTimes?.eidNamaz || []).filter(Boolean);

  const breadcrumbItems = [
    { label: t("universal_dashboard_text"), to: "/user-dashboard" },
    { label: t("prayerTimes_breadcrumb_title") },
  ];

  const exportToCSV = () => {
    if (!days.length) return;
    const headers = ["Date", "Imsak", "Fajr Athan", "Fajr Iqamah", "Sunrise", "Ishraq", "Zawal",
      ...PRAYERS.slice(1).flatMap((p) => [`${p} Athan`, `${p} Iqamah`])];
    const rows = days.map((d) => [
      d.date?.gregorian?.date, d.specialSalah?.imsak || "", athanOf(d, "Fajr"), iqamahOf(d, "Fajr"),
      athanOf(d, "Sunrise"), d.specialSalah?.ishraq || "", d.specialSalah?.zawal || "",
      ...PRAYERS.slice(1).flatMap((p) => [athanOf(d, p), iqamahOf(d, p)]),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `prayer-times-${selectedYear}-${pad(selectedMonth.id)}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const todayKey = dashed(now);

  return (
    <section className="min-h-[86vh] bg-gradient-to-b from-slate-50 to-gray-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <Header title={t("prayerTimes")} breadcrumbItems={breadcrumbItems} />
        </div>

        {today && (
          <div className="rounded-xl bg-white p-4 shadow">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">Today</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {["Fajr", "Sunrise", ...PRAYERS.slice(1)].map((p) => (
                <div key={p} className="rounded-lg bg-slate-50 px-3 py-2">
                  <div className="text-xs font-medium text-slate-500">{p}</div>
                  <div className="text-lg font-semibold tabular-nums text-slate-800">{f.time(athanOf(today, p))}</div>
                  {p !== "Sunrise" && <div className="text-xs text-emerald-700">Iqamah {f.time(iqamahOf(today, p))}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          <Card to="/user-dashboard/prayer-times/athan" icon={<Clock className="h-5 w-5" />} tone="bg-emerald-100 text-emerald-600"
            title={t("athanTimes")} text="How the athan times are worked out, or your own timetable." />
          <Card to="/user-dashboard/prayer-times/iqamah" icon={<Users className="h-5 w-5" />} tone="bg-blue-100 text-blue-600"
            title={t("iqamahTimes")} text="Minutes after the athan, fixed times, or your own timetable." />
          <Card to="/user-dashboard/prayer-times/jummah" icon={<CalendarDays className="h-5 w-5" />} tone="bg-purple-100 text-purple-600"
            title={t("jummahTimes")} text={t("fridayPrayerSchedule")}>
            {jummah.length ? (
              <ul className="space-y-1">
                {jummah.map((j, i) => (
                  <li key={i} className="tabular-nums">
                    {jummah.length > 1 ? `${i + 1}. ` : ""}Khutbah {f.time(j.athanTime)}, iqamah {f.time(j.iqamahTime)}
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-slate-500">Not set yet</span>
            )}
          </Card>
          <Card to="/user-dashboard/prayer-times/Special" icon={<Moon className="h-5 w-5" />} tone="bg-amber-100 text-amber-600"
            title={t("specialSalah")} text="Imsak, Ishraq, Zawal, Taraweeh and Eid prayers.">
            <p>Eid: {eid.length ? eid.map(f.time).join(", ") : <span className="text-slate-500">not set</span>}</p>
            {today?.specialSalah?.taraweeh && <p>Taraweeh today: {f.time(today.specialSalah.taraweeh)}</p>}
          </Card>
        </div>

        <div className="flex justify-start items-end flex-wrap gap-3">
          <div>
            <Label htmlFor="month" text="Month" />
            <MonthDropdown selectedOption={selectedMonth} options={monthOptions} onSelect={setSelectedMonth} title="Choose month" />
          </div>
          <div>
            <Label htmlFor="year" text="Year" />
            <input
              id="year"
              type="number"
              min="2000"
              max="2100"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="w-28 py-2.5 px-4 text-sm font-medium rounded-lg border border-gray-400 bg-white text-gray-800 shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <button
            type="button"
            onClick={exportToCSV}
            disabled={!days.length}
            className="px-5 py-2.5 text-sm bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            Export to CSV
          </button>
        </div>

        {isFetching ? (
          <PrayerTimeTableSkeleton />
        ) : !days.length ? (
          <p className="py-8 text-center text-gray-600">
            {error ? error?.data?.message || "Couldn't load the prayer times." : "No prayer times for this month. If you imported a timetable, it doesn't cover this month."}
          </p>
        ) : (
          <div className="shadow-sm border border-gray-300 rounded-lg overflow-x-auto bg-white">
            <table className="w-full table-auto text-sm text-left">
              <thead className="bg-gray-100 text-gray-600 font-medium border-b border-gray-300">
                <tr>
                  {/* The date stays in view while the times scroll sideways. */}
                  <th className="sticky left-0 z-10 bg-gray-100 py-3 px-4 shadow-[1px_0_0_#e5e7eb]">Date</th>
                  <th className="py-3 px-4">Imsak</th>
                  <th className="py-3 px-4">Fajr<span className="block text-xs font-normal text-gray-500">Athan / Iqamah</span></th>
                  <th className="py-3 px-4">Sunrise</th>
                  <th className="py-3 px-4">Ishraq</th>
                  <th className="py-3 px-4">Zawal</th>
                  {PRAYERS.slice(1).map((p) => (
                    <th key={p} className="py-3 px-4">{p}<span className="block text-xs font-normal text-gray-500">Athan / Iqamah</span></th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-gray-700 divide-y">
                {days.map((d) => {
                  const date = d.date?.gregorian?.date;
                  const isToday = date === todayKey;
                  const both = (p) => (
                    <td className="px-4 py-3 whitespace-nowrap tabular-nums">
                      {f.time(athanOf(d, p))} <span className="text-gray-400">/</span> <span className="text-emerald-700">{f.time(iqamahOf(d, p))}</span>
                    </td>
                  );
                  return (
                    <tr key={date} className={isToday ? "bg-blue-50" : ""} aria-current={isToday ? "date" : undefined}>
                      <td className={`sticky left-0 z-10 shadow-[1px_0_0_#e5e7eb] ${isToday ? "bg-blue-50" : "bg-white"} py-3 px-4 whitespace-nowrap font-medium text-gray-800`}>
                        {f.date(date)}
                        {isToday && <span className="ml-2 rounded bg-blue-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">Today</span>}
                      </td>
                      <td className="px-4 py-3 tabular-nums whitespace-nowrap">{f.time(d.specialSalah?.imsak)}</td>
                      {both("Fajr")}
                      <td className="px-4 py-3 tabular-nums whitespace-nowrap">{f.time(athanOf(d, "Sunrise"))}</td>
                      <td className="px-4 py-3 tabular-nums whitespace-nowrap">{f.time(d.specialSalah?.ishraq)}</td>
                      <td className="px-4 py-3 tabular-nums whitespace-nowrap">{f.time(d.specialSalah?.zawal)}</td>
                      {PRAYERS.slice(1).map((p) => <React.Fragment key={p}>{both(p)}</React.Fragment>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default PrayerTimeMain;
